import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  CheckCircleOutline,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  EventOutlined,
  FilterListOutlined,
  TodayOutlined,
  PetsOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import { APP_CONFIG } from "../../config/appConfig";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { cashService, groomingService } from "../../services";
import GroomingDiagnosticDialog from "./components/GroomingDiagnosticDialog";

const APPOINTMENT_STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "SCHEDULED", label: "Programada" },
  { value: "CONFIRMED", label: "Confirmada" },
  { value: "CHECKED_IN", label: "Recepcionada" },
  { value: "NO_SHOW", label: "No llego" },
  { value: "CANCELED", label: "Cancelada" },
  { value: "CONVERTED", label: "Convertida" },
  { value: "COMPLETED", label: "Completada" },
];

const APPOINTMENT_DATE_FIELD_OPTIONS = [
  { value: "scheduled_start", label: "Programada" },
  { value: "scheduled_end", label: "Final estimada" },
  { value: "created_at", label: "Creada" },
  { value: "confirmed_at", label: "Confirmada" },
  { value: "checked_in_at", label: "Recepcion" },
  { value: "canceled_at", label: "Cancelacion" },
];

const BOOL_FILTER_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "true", label: "Si" },
  { value: "false", label: "No" },
];

const PHONE_MAX_LENGTH = 20;
const SEARCH_DEBOUNCE_MS = 350;
const CUSTOMER_LOOKUP_DEBOUNCE_MS = 350;
const MIN_PHONE_LOOKUP_LENGTH = 8;
const GROOMING_TIME_ZONE = APP_CONFIG.timeZone;
const GROOMING_LOCALE = "es-GT";
const APPOINTMENT_BLOCKING_STATUSES = new Set(["SCHEDULED", "CONFIRMED"]);
const GROOMING_SLOT_MINUTES = 30;
const GROOMING_BUSINESS_START_HOUR = 8;
const GROOMING_BUSINESS_END_HOUR = 18;
const MISSED_ALERT_DISMISS_STORAGE_PREFIX = "grooming.missed.dismissed";
const normalizePhoneValue = (value) => String(value || "").replace(/[^\d]/g, "").slice(0, PHONE_MAX_LENGTH);
dayjs.locale("es");

const buildAppointmentForm = () => ({
  phone_number: "",
  customer: null,
  contacts: [],
  responsible_contact_id: "",
  responsible_contact: null,
  pets: [],
  pet_id: "",
  scheduled_date: null,
  scheduled_time: "",
  requested_service_type_id: "",
  requested_service_name: "",
  complexity_level: "",
  duration_minutes: "60",
  assigned_to_id: "",
  requested_notes: "",
});

const buildRescheduleForm = () => ({
  scheduled_date: null,
  scheduled_time: "",
  requested_service_type_id: "",
  complexity_level: "",
  duration_minutes: "60",
  assigned_to_id: "",
  requested_notes: "",
});

const buildQuickCustomerForm = () => ({
  name: "",
  email: "",
});

const buildQuickPetForm = () => ({
  name: "",
  species: "DOG",
  breed: "",
  size_category: "UNKNOWN",
});

const PET_SPECIES_OPTIONS = [
  { value: "DOG", label: "Perro" },
  { value: "CAT", label: "Gato" },
  { value: "OTHER", label: "Otro" },
];

const PET_SIZE_OPTIONS = [
  { value: "UNKNOWN", label: "No definido" },
  { value: "XS", label: "XS" },
  { value: "S", label: "Pequeno" },
  { value: "M", label: "Mediano" },
  { value: "L", label: "Grande" },
  { value: "XL", label: "XL" },
];

const getPetSizeLabel = (value) =>
  PET_SIZE_OPTIONS.find((option) => option.value === value)?.label || "No definido";

const STATUS_COLOR_PROGRAMMED = "#378ADD";
const STATUS_COLOR_RECEIVED = "#EF9F27";
const STATUS_COLOR_IN_PROGRESS = "#7F77DD";
const STATUS_COLOR_COMPLETED = "#639922";

const TIMELINE_STATUS_TABS = [
  { key: "ALL", label: "Todas" },
  { key: "SCHEDULED", label: "Programadas" },
  { key: "CHECKED_IN", label: "Recepcionadas" },
  { key: "IN_PROGRESS", label: "En proceso" },
  { key: "COMPLETED", label: "Completadas" },
];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(GROOMING_LOCALE, {
    timeZone: GROOMING_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toQueryDate = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const GUATEMALA_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: GROOMING_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const getGuatemalaNowSnapshot = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { dateKey: "", minutesOfDay: null };
  }
  const parts = GUATEMALA_DATE_TIME_FORMATTER.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const day = parts.find((part) => part.type === "day")?.value || "";
  const hour = Number(parts.find((part) => part.type === "hour")?.value || NaN);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || NaN);
  const dateKey = `${year}-${month}-${day}`;
  const minutesOfDay = Number.isInteger(hour) && Number.isInteger(minute) ? ((hour * 60) + minute) : null;
  return { dateKey, minutesOfDay };
};

const buildGuatemalaScheduledIso = (dateKey, timeValue) => {
  const normalizedDate = String(dateKey || "").trim();
  const normalizedTime = String(timeValue || "").trim();
  if (!normalizedDate || !normalizedTime) return null;
  return `${normalizedDate}T${normalizedTime}:00-06:00`;
};

const getServiceOptionId = (option) => (option?.id ? String(option.id) : "");
const getServiceOptionLabel = (option) =>
  String(option?.name || option?.label || option?.value || "").trim();

const findComplexityValueByLabel = (options, label) => {
  const normalizedLabel = String(label || "").trim().toLowerCase();
  if (!normalizedLabel) return "";
  const match = options.find(
    (option) =>
      String(option?.label || "")
        .trim()
        .toLowerCase() === normalizedLabel
  );
  return match?.value || "";
};

const parseAppointmentNotes = (notesValue, complexityOptions = []) => {
  const lines = String(notesValue || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  let complexityLabel = "";
  const freeLines = [];

  lines.forEach((line) => {
    if (/^tamano:/i.test(line)) {
      return;
    }
    if (/^complejidad:/i.test(line)) {
      complexityLabel = line.replace(/^complejidad:/i, "").trim();
      return;
    }
    freeLines.push(line);
  });

  return {
    complexity_level: findComplexityValueByLabel(complexityOptions, complexityLabel),
    comment: freeLines.join("\n").trim(),
  };
};

const buildAppointmentNotes = ({ petSizeLabel = "", complexityLabel = "", comment = "" }) =>
  [petSizeLabel ? `Tamano: ${petSizeLabel}` : "", complexityLabel ? `Complejidad: ${complexityLabel}` : "", String(comment || "").trim()]
    .filter(Boolean)
    .join("\n")
    .trim();

const slotToMinutes = (slot) => {
  const [hoursText, minutesText] = String(slot || "").split(":");
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  return (hours * 60) + minutes;
};

const DEFAULT_BUSINESS_START = "08:00";
const DEFAULT_BUSINESS_END = "18:00";

const buildDefaultSlotValues = () => {
  const values = [];
  const from = slotToMinutes(DEFAULT_BUSINESS_START) || (GROOMING_BUSINESS_START_HOUR * 60);
  const to = slotToMinutes(DEFAULT_BUSINESS_END) || (GROOMING_BUSINESS_END_HOUR * 60);
  for (let minuteMark = from; minuteMark < to; minuteMark += GROOMING_SLOT_MINUTES) {
    const hh = Math.floor(minuteMark / 60);
    const mm = minuteMark % 60;
    values.push(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }
  return values;
};

const DEFAULT_GROOMING_SLOTS = buildDefaultSlotValues();

const getSlotsFromAvailability = (availability) => {
  const slots = toArray(availability?.business_hours?.slots)
    .map((slot) => String(slot || "").trim())
    .filter(Boolean);
  return slots.length > 0 ? slots : DEFAULT_GROOMING_SLOTS;
};

const getSlotOccupancyMapFromAvailability = (availability) => {
  const map = {};
  toArray(availability?.slots).forEach((slot) => {
    const time = String(slot?.time || "").trim();
    if (!time) return;
    map[time] = slot;
  });
  return map;
};

const getBusySlotsFromAvailability = (availability) =>
  new Set(
    toArray(availability?.busy_slots)
      .map((slot) => String(slot || "").trim())
      .filter(Boolean)
  );

const getBusinessSlotMinutes = (availability) => {
  const parsed = Number(availability?.business_hours?.slot_minutes);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return GROOMING_SLOT_MINUTES;
};

const getBusinessStartMinutes = (availability) =>
  slotToMinutes(String(availability?.business_hours?.start || DEFAULT_BUSINESS_START));

const getBusinessEndMinutes = (availability) =>
  slotToMinutes(String(availability?.business_hours?.end || DEFAULT_BUSINESS_END));

const validateScheduleDateTime = (dateValue, timeValue, options = {}) => {
  const dateKey = toQueryDate(dateValue);
  const normalizedTime = String(timeValue || "").trim();
  const scheduledIso = buildGuatemalaScheduledIso(dateKey, normalizedTime);
  if (!scheduledIso) {
    return { iso: null, error: "Fecha y hora invalidas." };
  }

  const availability = options.availability || null;
  const slotMinutes = slotToMinutes(normalizedTime);
  const businessSlotMinutes = getBusinessSlotMinutes(availability);
  if (slotMinutes == null || slotMinutes % businessSlotMinutes !== 0) {
    return { iso: scheduledIso, error: `La hora debe estar alineada a slots de ${businessSlotMinutes} minutos.` };
  }

  const openingMinutes = getBusinessStartMinutes(availability);
  const closingMinutes = getBusinessEndMinutes(availability);
  const lastSlotMinuteMark =
    Number.isInteger(closingMinutes) && Number.isInteger(businessSlotMinutes)
      ? (closingMinutes - businessSlotMinutes)
      : null;
  if (
    openingMinutes == null
    || closingMinutes == null
    || lastSlotMinuteMark == null
    || lastSlotMinuteMark < openingMinutes
    || slotMinutes < openingMinutes
    || slotMinutes > lastSlotMinuteMark
  ) {
    const startLabel = String(availability?.business_hours?.start || DEFAULT_BUSINESS_START);
    const endLabel = String(availability?.business_hours?.end || DEFAULT_BUSINESS_END);
    const safeLastSlotMinuteMark = Number.isInteger(lastSlotMinuteMark)
      ? lastSlotMinuteMark
      : ((slotToMinutes(endLabel) || (18 * 60)) - businessSlotMinutes);
    const lastSlotLabel = `${String(Math.floor(safeLastSlotMinuteMark / 60)).padStart(2, "0")}:${String(safeLastSlotMinuteMark % 60).padStart(2, "0")}`;
    return {
      iso: scheduledIso,
      error: `La hora de grooming debe estar entre ${startLabel} y ${endLabel} (ultimo slot inicia a las ${lastSlotLabel}).`,
    };
  }

  const nowSnapshot = getGuatemalaNowSnapshot(options.now || new Date());
  if (dateKey < nowSnapshot.dateKey) {
    return { iso: scheduledIso, error: "La fecha no puede ser anterior a hoy." };
  }
  if (
    dateKey === nowSnapshot.dateKey
    && Number.isInteger(nowSnapshot.minutesOfDay)
    && slotMinutes < nowSnapshot.minutesOfDay
  ) {
    return { iso: scheduledIso, error: "La hora no puede ser anterior a la hora actual." };
  }
  const busySlots = getBusySlotsFromAvailability(options.availability);
  const excludeBusySlot = String(options.excludeBusySlot || "").trim();
  if (busySlots.has(normalizedTime) && normalizedTime !== excludeBusySlot) {
    return { iso: scheduledIso, error: "Ese slot esta lleno." };
  }
  return { iso: scheduledIso, error: "" };
};

const buildAvailabilityHelpText = (availability) => {
  const start = String(availability?.business_hours?.start || DEFAULT_BUSINESS_START);
  const end = String(availability?.business_hours?.end || DEFAULT_BUSINESS_END);
  const slotMinutes = getBusinessSlotMinutes(availability);
  return `Slots de ${slotMinutes} minutos entre ${start} y ${end}.`;
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

const sortPetsByName = (pets) =>
  [...(Array.isArray(pets) ? pets : [])].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), "es", { sensitivity: "base" })
  );

const getAppointmentStatusMeta = (appointment) => {
  const normalized = String(appointment?.status || appointment || "").toUpperCase();
  const timeState = String(appointment?.time_state || "").toUpperCase();
  if (timeState === "MISSED" && ["SCHEDULED", "CONFIRMED"].includes(normalized)) {
    return { label: "No llego", color: "default", accentColor: "disabled", categoryKey: "CANCELED" };
  }
  if (timeState === "DELAYED" && ["SCHEDULED", "CONFIRMED"].includes(normalized)) {
    return {
      label: "Retrasada",
      color: "warning",
      accentColor: STATUS_COLOR_RECEIVED,
      categoryKey: "SCHEDULED",
      isDelayed: true,
    };
  }
  if (normalized === "CONFIRMED") {
    return { label: "Confirmada", color: "info", accentColor: STATUS_COLOR_PROGRAMMED, categoryKey: "SCHEDULED" };
  }
  if (normalized === "CHECKED_IN") {
    return { label: "Recepcionada", color: "warning", accentColor: STATUS_COLOR_RECEIVED, categoryKey: "CHECKED_IN" };
  }
  if (normalized === "CONVERTED") {
    const order = appointment?.order || appointment?.order_detail || null;
    const orderUiStatus = String(order?.ui_status || "").toUpperCase();
    const paymentStatus = String(order?.payment_status || "").toUpperCase();
    const saleStatus = String(order?.sale?.status || "").toUpperCase();
    const isCancelledCycle =
      orderUiStatus === "CANCELADO" ||
      paymentStatus === "VOID" ||
      ["VOIDED", "CANCELED"].includes(saleStatus);
    if (isCancelledCycle) {
      return { label: "Cancelada", color: "default", accentColor: "disabled", categoryKey: "CANCELED" };
    }
    const isFinalizedCycle =
      orderUiStatus === "COBRADO" ||
      paymentStatus === "PAID" ||
      ["COMPLETED", "RETURNED"].includes(saleStatus);
    if (isFinalizedCycle) {
      return { label: "Finalizado", color: "success", accentColor: STATUS_COLOR_COMPLETED, categoryKey: "COMPLETED" };
    }
    return { label: "En proceso", color: "default", accentColor: STATUS_COLOR_IN_PROGRESS, categoryKey: "IN_PROGRESS" };
  }
  if (normalized === "COMPLETED") {
    return { label: "Completada", color: "success", accentColor: STATUS_COLOR_COMPLETED, categoryKey: "COMPLETED" };
  }
  if (normalized === "CANCELED") {
    return { label: "Cancelada", color: "default", accentColor: "disabled", categoryKey: "CANCELED" };
  }
  if (normalized === "NO_SHOW") {
    return { label: "No llego", color: "default", accentColor: "disabled", categoryKey: "CANCELED" };
  }
  return { label: "Programada", color: "default", accentColor: STATUS_COLOR_PROGRAMMED, categoryKey: "SCHEDULED" };
};

const startOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value || Date.now());
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (value, amount) => {
  const next = startOfDay(value);
  next.setDate(next.getDate() + amount);
  return next;
};

const isSameDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  getGuatemalaNowSnapshot(left).dateKey === getGuatemalaNowSnapshot(right).dateKey;

const formatAgendaDayLabel = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const baseLabel = date.toLocaleDateString(GROOMING_LOCALE, {
    timeZone: GROOMING_TIME_ZONE,
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  return isSameDay(date, new Date()) ? `Hoy · ${baseLabel}` : baseLabel;
};

const formatAgendaTime = (value) => {
  if (!value) return "--:--";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString(GROOMING_LOCALE, {
    timeZone: GROOMING_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toTimeInputValue = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    timeZone: GROOMING_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatAgendaRange = (startValue, endValue) => {
  const startLabel = formatAgendaTime(startValue);
  const endLabel = endValue ? formatAgendaTime(endValue) : "";
  return endLabel && endLabel !== "--:--" ? `${startLabel} - ${endLabel}` : startLabel;
};

const getAppointmentAccentColor = (statusMeta, theme) =>
  statusMeta?.accentColor === "disabled"
    ? theme.palette.text.disabled
    : statusMeta?.accentColor || theme.palette.text.primary;

const getAppointmentCategoryKey = (appointment) => getAppointmentStatusMeta(appointment).categoryKey || "SCHEDULED";
const isAppointmentInvalidForOrderConversion = (appointment) => {
  if (!appointment) return true;
  const status = String(appointment?.status || "").toUpperCase();
  const timeState = String(appointment?.time_state || "").toUpperCase();
  if (["CANCELED", "NO_SHOW", "COMPLETED"].includes(status)) return true;
  if (timeState === "MISSED") return true;
  return false;
};

const getMinutesUntilAppointment = (value, now) => {
  const appointmentDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(appointmentDate.getTime())) return null;
  return Math.round((appointmentDate.getTime() - now.getTime()) / 60000);
};

function MetricCard({ label, value, color, theme }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Typography variant="h5" sx={{ color, fontWeight: 400, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color === "disabled" ? theme.palette.text.disabled : color,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    </Paper>
  );
}

function NowMarker({ nowLabel }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
      <Box sx={{ width: 42 }} />
      <Box sx={{ width: 24 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
        <Divider sx={{ flex: 1, borderColor: "error.main" }} />
        <Typography variant="caption" color="error">
          ahora
        </Typography>
        <Typography variant="caption" color="error">
          {nowLabel}
        </Typography>
        <Divider sx={{ flex: 1, borderColor: "error.main" }} />
      </Stack>
    </Stack>
  );
}

function TimelineAppointmentRow({
  appointment,
  now,
  theme,
  canManage,
  convertingId,
  cashStatusKnown,
  hasOpenCashSession,
  onConvert,
  onOpenDetail,
  onOpenOrder,
  onReschedule,
}) {
  const statusMeta = getAppointmentStatusMeta(appointment);
  const scheduledStartDate = new Date(appointment?.scheduled_start);
  const minutesUntil = getMinutesUntilAppointment(scheduledStartDate, now);
  const isCancelled = ["CANCELED", "NO_SHOW"].includes(String(appointment?.status || "").toUpperCase());
  const conversionBlocked = isAppointmentInvalidForOrderConversion(appointment);
  const canReschedule = Boolean(appointment?.can_reschedule);
  const isArrivingSoon =
    Number.isFinite(minutesUntil) &&
    minutesUntil >= 0 &&
    minutesUntil <= 30 &&
    ["SCHEDULED", "CONFIRMED"].includes(String(appointment?.status || "").toUpperCase());
  const accentColor = isArrivingSoon
    ? STATUS_COLOR_RECEIVED
    : getAppointmentAccentColor(statusMeta, theme);
  const avatarLabel = String(appointment?.pet?.name || "?").trim().charAt(0).toUpperCase() || "?";
  const assignedLabel = appointment?.assigned_to?.name || "Sin groomer";
  const assignedInitials = String(assignedLabel)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

  return (
    <Stack direction="row" spacing={1.5} alignItems="stretch">
      <Box sx={{ width: 42, pt: 0.5 }}>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "right" }}>
          {formatAgendaTime(appointment?.scheduled_start)}
        </Typography>
      </Box>

      <Stack alignItems="center" sx={{ width: 24, flexShrink: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: accentColor,
            mt: 0.75,
            mb: 0.5,
          }}
        />
        <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(accentColor, 0.4) }} />
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          opacity: isCancelled ? 0.5 : 1,
          borderRadius: `${theme.shape.borderRadius}px`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "0 auto 0 0",
            width: 3,
            bgcolor: accentColor,
            borderRadius: "3px 0 0 3px",
          }}
        />

        <Stack direction="row" spacing={1.25} sx={{ py: 1.35, pl: 1.75, pr: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 14,
              bgcolor: alpha(accentColor, 0.14),
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {avatarLabel}
          </Avatar>

          <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    textDecoration: isCancelled ? "line-through" : "none",
                  }}
                  noWrap
                >
                  {appointment?.pet?.name || "Mascota"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {appointment?.pet?.breed || "Sin raza"}
                </Typography>
                {isArrivingSoon ? (
                  <Chip
                    size="small"
                    label={`Llega en ~${minutesUntil} min`}
                    color="warning"
                    sx={{ height: 20 }}
                  />
                ) : null}
              </Stack>

              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={statusMeta.label}
                  sx={{
                    height: 20,
                    borderColor: alpha(accentColor, 0.32),
                    bgcolor: alpha(accentColor, 0.1),
                    color: accentColor,
                  }}
                  variant="outlined"
                />
                {appointment?.is_rescheduled ? (
                  <Chip
                    size="small"
                    label="Reprogramada"
                    color="secondary"
                    variant="outlined"
                    sx={{ height: 20 }}
                  />
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  {formatAgendaRange(appointment?.scheduled_start, appointment?.scheduled_end)}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ xs: "flex-start", md: "center" }} sx={{ flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {appointment?.customer?.name || "Cliente"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {appointment?.requested_service_name || "Servicio no especificado"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ·
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                avatar={
                  <Avatar sx={{ width: 18, height: 18, fontSize: 10, bgcolor: alpha(accentColor, 0.12), color: accentColor }}>
                    {assignedInitials}
                  </Avatar>
                }
                label={assignedLabel}
                sx={{ height: 22 }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              {appointment?.order?.id ? (
                <Button size="small" variant="outlined" onClick={() => onOpenOrder(appointment.order.id)}>
                  Ver orden #{appointment.order.id}
                </Button>
              ) : conversionBlocked ? (
                <Button size="small" variant="text" onClick={() => onOpenDetail(appointment)}>
                  Ver detalle
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      !canManage ||
                      conversionBlocked ||
                      convertingId === appointment?.id ||
                      (cashStatusKnown && !hasOpenCashSession)
                    }
                    onClick={() => onConvert(appointment)}
                  >
                    {convertingId === appointment?.id ? "Convirtiendo..." : "Convertir a orden"}
                  </Button>
                  {canManage && canReschedule ? (
                    <Button
                      size="small"
                      variant={statusMeta?.isDelayed ? "contained" : "outlined"}
                      color={statusMeta?.isDelayed ? "warning" : "primary"}
                      onClick={() => onReschedule(appointment)}
                    >
                      Reprogramar
                    </Button>
                  ) : null}
                  <Button size="small" variant="text" onClick={() => onOpenDetail(appointment)}>
                    Ver detalle
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}

function GroomingAppointmentsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewPage = hasPermission(effectivePerms, "grooming_view", isSuperuser);
  const canManage = hasPermission(effectivePerms, "grooming_manage", isSuperuser);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState(buildAppointmentForm());
  const [createErrors, setCreateErrors] = useState({});
  const [appointmentConflict, setAppointmentConflict] = useState(null);
  const [contextData, setContextData] = useState({
    groomers: [],
    service_types: [],
    complexity_levels: [],
    statuses: [],
    missed_appointments: [],
  });
  const [contextLoading, setContextLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupStatus, setLookupStatus] = useState("idle");
  const [lookupMessage, setLookupMessage] = useState("");
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [quickCustomerForm, setQuickCustomerForm] = useState(buildQuickCustomerForm());
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [quickPetForm, setQuickPetForm] = useState(buildQuickPetForm());
  const [creatingPet, setCreatingPet] = useState(false);
  const [showQuickPetForm, setShowQuickPetForm] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState(buildRescheduleForm());
  const [rescheduleErrors, setRescheduleErrors] = useState({});
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [createAvailability, setCreateAvailability] = useState(null);
  const [loadingCreateAvailability, setLoadingCreateAvailability] = useState(false);
  const [rescheduleAvailability, setRescheduleAvailability] = useState(null);
  const [loadingRescheduleAvailability, setLoadingRescheduleAvailability] = useState(false);
  const [cashStatusKnown, setCashStatusKnown] = useState(false);
  const [hasOpenCashSession, setHasOpenCashSession] = useState(false);
  const [diagnosticDialog, setDiagnosticDialog] = useState({
    open: false,
    orderId: null,
    initialOrder: null,
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [petFilter, setPetFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [dateField, setDateField] = useState("scheduled_start");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [hasOrderFilter, setHasOrderFilter] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [dayPickerAnchorEl, setDayPickerAnchorEl] = useState(null);
  const [statusTab, setStatusTab] = useState("ALL");
  const [now, setNow] = useState(() => new Date());
  const [missedAlertExpanded, setMissedAlertExpanded] = useState(false);
  const [missedAlertDismissed, setMissedAlertDismissed] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const groomerOptions = useMemo(() => toArray(contextData?.groomers), [contextData?.groomers]);
  const serviceTypeOptions = useMemo(() => toArray(contextData?.service_types), [contextData?.service_types]);
  const complexityOptions = useMemo(() => toArray(contextData?.complexity_levels), [contextData?.complexity_levels]);
  const missedAppointments = useMemo(
    () => toArray(contextData?.missed_appointments),
    [contextData?.missed_appointments]
  );
  const missedAlertSignature = useMemo(
    () =>
      missedAppointments
        .map((row) => `${row?.id || ""}:${row?.scheduled_start || ""}`)
        .filter(Boolean)
        .join("|"),
    [missedAppointments]
  );

  const clearCreateFieldError = useCallback((field) => {
    if (!field && field !== "") return;
    setCreateErrors((prev) => {
      if (!prev[field] && !prev.form) return prev;
      const next = { ...prev };
      if (field && next[field]) {
        delete next[field];
      }
      if (next.form) {
        delete next.form;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!branchId || !missedAlertSignature) {
      setMissedAlertDismissed(false);
      setMissedAlertExpanded(false);
      return;
    }
    const storageKey = `${MISSED_ALERT_DISMISS_STORAGE_PREFIX}:${branchId}`;
    const dismissedSignature = window.localStorage.getItem(storageKey) || "";
    const isDismissed = dismissedSignature === missedAlertSignature;
    setMissedAlertDismissed(isDismissed);
    setMissedAlertExpanded(!isDismissed);
  }, [branchId, missedAlertSignature]);

  const loadCashStatus = useCallback(async () => {
    if (!branchId) {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
      return;
    }

    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      const hasOpenSession = Boolean(data?.session && data.session.status === "OPEN");
      setCashStatusKnown(true);
      setHasOpenCashSession(hasOpenSession);
    } catch {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
    }
  }, [branchId]);

  const ensureOpenCashSession = useCallback(
    async (message) => {
      if (!branchId) return false;
      try {
        const data = await cashService.currentSession({ branch_id: branchId });
        const hasOpenSession = Boolean(data?.session && data.session.status === "OPEN");
        setCashStatusKnown(true);
        setHasOpenCashSession(hasOpenSession);
        if (!hasOpenSession) {
          showSnack(message, "warning");
          return false;
        }
        return true;
      } catch {
        return true;
      }
    },
    [branchId, showSnack]
  );

  const loadAppointmentContext = useCallback(
    async ({ silent = false } = {}) => {
      if (!branchId || !canViewPage) {
        setContextData({
          groomers: [],
          service_types: [],
          complexity_levels: [],
          statuses: [],
          missed_appointments: [],
        });
        return;
      }
      if (!silent) setContextLoading(true);
      try {
        const data = await groomingService.getAppointmentContext({ branch_id: branchId });
        setContextData({
          groomers: toArray(data?.groomers),
          service_types: toArray(data?.service_types),
          complexity_levels: toArray(data?.complexity_levels),
          statuses: toArray(data?.statuses),
          missed_appointments: toArray(data?.missed_appointments),
        });
      } catch (error) {
        if (!silent) {
          showSnack(extractErrorMessage(error, "No se pudo cargar el contexto de citas grooming."), "error");
        }
      } finally {
        if (!silent) setContextLoading(false);
      }
    },
    [branchId, canViewPage, showSnack]
  );

  const loadRows = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setRows([]);
      return;
    }

    const hasCustomDateRange = Boolean(dateFrom || dateTo);
    const effectiveDateField = hasCustomDateRange ? dateField || "scheduled_start" : "scheduled_start";
    const effectiveDateFrom = hasCustomDateRange ? toQueryDate(dateFrom) : toQueryDate(selectedDay);
    const effectiveDateTo = hasCustomDateRange ? toQueryDate(dateTo) : toQueryDate(selectedDay);

    setLoading(true);
    try {
      const data = await groomingService.listAppointments({
        branch_id: branchId,
        q: debouncedSearch || undefined,
        status: statusFilter || undefined,
        customer: customerFilter || undefined,
        pet: petFilter || undefined,
        assigned_to: assignedToFilter || undefined,
        date_field: effectiveDateField,
        date_from: effectiveDateFrom,
        date_to: effectiveDateTo,
        has_order: hasOrderFilter || undefined,
      });
      setRows(toArray(data));
      loadAppointmentContext({ silent: true });
    } catch (error) {
      setRows([]);
      showSnack(extractErrorMessage(error, "No se pudieron cargar las citas grooming."), "error");
    } finally {
      setLoading(false);
    }
  }, [
    assignedToFilter,
    branchId,
    canViewPage,
    customerFilter,
    dateField,
    dateFrom,
    dateTo,
    hasOrderFilter,
    petFilter,
    selectedDay,
    debouncedSearch,
    loadAppointmentContext,
    showSnack,
    statusFilter,
  ]);

  const fetchAppointmentAvailability = useCallback(
    async (dateValue, extraParams = {}) => {
      if (!branchId) return null;
      const dateParam = toQueryDate(dateValue);
      if (!dateParam) return null;
      return groomingService.getAppointmentAvailability({
        branch_id: branchId,
        date: dateParam,
        ...extraParams,
      });
    },
    [branchId]
  );

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    loadAppointmentContext();
  }, [loadAppointmentContext]);

  useEffect(() => {
    loadCashStatus();
  }, [loadCashStatus]);

  useEffect(() => {
    if (!createDialogOpen || !branchId) return undefined;
    if (!createForm.scheduled_date) {
      setCreateAvailability(null);
      setLoadingCreateAvailability(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingCreateAvailability(true);
      try {
        const data = await fetchAppointmentAvailability(createForm.scheduled_date);
        if (cancelled) return;
        setCreateAvailability(data || null);
      } catch {
        if (cancelled) return;
        setCreateAvailability(null);
      } finally {
        if (!cancelled) {
          setLoadingCreateAvailability(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [branchId, createDialogOpen, createForm.scheduled_date, fetchAppointmentAvailability]);

  useEffect(() => {
    if (!rescheduleDialogOpen || !branchId) return undefined;
    if (!rescheduleForm.scheduled_date) {
      setRescheduleAvailability(null);
      setLoadingRescheduleAvailability(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRescheduleAvailability(true);
      try {
        const data = await fetchAppointmentAvailability(
          rescheduleForm.scheduled_date,
          rescheduleTarget?.id ? { exclude_appointment_id: rescheduleTarget.id } : {}
        );
        if (cancelled) return;
        setRescheduleAvailability(data || null);
      } catch {
        if (cancelled) return;
        setRescheduleAvailability(null);
      } finally {
        if (!cancelled) {
          setLoadingRescheduleAvailability(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    branchId,
    fetchAppointmentAvailability,
    rescheduleDialogOpen,
    rescheduleForm.scheduled_date,
    rescheduleTarget?.id,
  ]);

  const handleDateChange = useCallback((field, value) => {
    if (field === "date_from") setDateFrom(value);
    if (field === "date_to") setDateTo(value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("");
    setCustomerFilter("");
    setPetFilter("");
    setAssignedToFilter("");
    setDateField("scheduled_start");
    setDateFrom(null);
    setDateTo(null);
    setHasOrderFilter("");
    setStatusTab("ALL");
  }, []);

  const resetCreateForm = useCallback(() => {
    setCreateForm(buildAppointmentForm());
    setCreateErrors({});
    setAppointmentConflict(null);
    setCreateAvailability(null);
    setLoadingCreateAvailability(false);
    setQuickCustomerForm(buildQuickCustomerForm());
    setQuickPetForm(buildQuickPetForm());
    setLookupStatus("idle");
    setLookupMessage("");
    setShowQuickPetForm(false);
  }, []);

  const closeCreateDialog = useCallback(() => {
    if (savingAppointment || lookupLoading || creatingCustomer || creatingPet) return;
    setCreateDialogOpen(false);
    resetCreateForm();
  }, [creatingCustomer, creatingPet, lookupLoading, resetCreateForm, savingAppointment]);

  const handleCreateFieldChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      if (field === "phone_number") {
        clearCreateFieldError("customer");
        clearCreateFieldError("pet_id");
        const normalizedPhone = normalizePhoneValue(value);
        setCreateForm((prev) => ({
          ...prev,
          phone_number: normalizedPhone,
          customer: null,
          pets: [],
          pet_id: "",
        }));
        setQuickCustomerForm(buildQuickCustomerForm());
        setQuickPetForm(buildQuickPetForm());
        setShowQuickPetForm(false);
        setLookupStatus("idle");
        setLookupMessage("");
        setAppointmentConflict(null);
        return;
      }

      clearCreateFieldError(field);
      if (field === "pet_id") {
        clearCreateFieldError("customer");
      }
      setCreateForm((prev) => ({
        ...prev,
        [field]: value,
        ...(field === "responsible_contact_id"
          ? { responsible_contact: prev.contacts.find((contact) => String(contact.id) === String(value)) || null }
          : {}),
      }));
      setAppointmentConflict(null);
    },
    [clearCreateFieldError]
  );

  const handleCreateDateChange = useCallback((field, value) => {
    clearCreateFieldError(field);
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "scheduled_date" ? { scheduled_time: "" } : {}),
    }));
    setAppointmentConflict(null);
  }, [clearCreateFieldError]);

  const handleQuickCustomerFieldChange = useCallback(
    (field) => (event) => {
      const value = field === "email" ? String(event.target.value || "").trim() : event.target.value;
      setQuickCustomerForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleQuickPetFieldChange = useCallback(
    (field) => (event) => {
      setQuickPetForm((prev) => ({ ...prev, [field]: event.target.value }));
    },
    []
  );

  useEffect(() => {
    if (!createDialogOpen || !branchId) return undefined;

    const phoneNumber = normalizePhoneValue(createForm.phone_number);
    if (!phoneNumber) {
      setLookupLoading(false);
      setLookupStatus("idle");
      setLookupMessage("");
      return undefined;
    }

    if (phoneNumber.length < MIN_PHONE_LOOKUP_LENGTH) {
      setLookupLoading(false);
      setLookupStatus("typing");
      setLookupMessage("Ingresa el telefono completo para detectar al cliente.");
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setLookupLoading(true);
      setLookupStatus("loading");
      setLookupMessage("");
      try {
        const data = await groomingService.lookupCustomerByPhone(phoneNumber, { branch_id: branchId });
        if (cancelled) return;
        const pets = sortPetsByName(toArray(data?.pets));
        const contacts = toArray(data?.contacts);
        const responsibleContact = data?.responsible_contact || contacts.find((contact) => contact?.is_primary) || null;
        clearCreateFieldError("customer");
        clearCreateFieldError("pet_id");
        setCreateForm((prev) => ({
          ...prev,
          phone_number: phoneNumber,
          customer: data?.customer || null,
          contacts,
          responsible_contact: responsibleContact,
          responsible_contact_id: responsibleContact?.id ? String(responsibleContact.id) : "",
          pets,
          pet_id: pets.some((pet) => String(pet.id) === String(prev.pet_id))
            ? String(prev.pet_id)
            : pets[0]?.id
              ? String(pets[0].id)
              : "",
        }));
        setQuickCustomerForm(buildQuickCustomerForm());
        setLookupStatus("found");
        setLookupMessage(
          pets.length === 0
            ? "Cliente detectado. Aun no tiene mascotas registradas en esta sucursal."
            : "Cliente detectado correctamente."
        );
      } catch (error) {
        if (cancelled) return;
        setCreateForm((prev) => ({
          ...prev,
          customer: null,
          contacts: [],
          responsible_contact: null,
          responsible_contact_id: "",
          pets: [],
          pet_id: "",
        }));

        if (error?.response?.status === 404) {
          setLookupStatus("not_found");
          setLookupMessage("No existe un cliente con ese telefono. Puedes crearlo aqui mismo.");
          return;
        }

        setLookupStatus("error");
        setLookupMessage(
          extractErrorMessage(error, "No se pudo validar el cliente por telefono en este momento.")
        );
      } finally {
        if (!cancelled) {
          setLookupLoading(false);
        }
      }
    }, CUSTOMER_LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [branchId, clearCreateFieldError, createDialogOpen, createForm.phone_number]);

  const handleCreateCustomer = useCallback(async () => {
    if (!branchId) return;
    const phoneNumber = normalizePhoneValue(createForm.phone_number);
    if (phoneNumber.length < MIN_PHONE_LOOKUP_LENGTH) {
      showSnack("Ingresa un telefono valido antes de crear el cliente.", "warning");
      return;
    }
    if (!String(quickCustomerForm.name || "").trim()) {
      showSnack("Debes ingresar el nombre del cliente.", "warning");
      return;
    }

    setCreatingCustomer(true);
    try {
      const data = await groomingService.createQuickCustomer({
        branch_id: branchId,
        name: String(quickCustomerForm.name || "").trim(),
        phone_number: phoneNumber,
        email: String(quickCustomerForm.email || "").trim() || undefined,
      });
      setCreateForm((prev) => ({
        ...prev,
        phone_number: phoneNumber,
        customer: data?.customer || null,
        contacts: toArray(data?.contacts),
        responsible_contact: toArray(data?.contacts).find((contact) => contact?.is_primary) || null,
        responsible_contact_id: toArray(data?.contacts).find((contact) => contact?.is_primary)?.id
          ? String(toArray(data?.contacts).find((contact) => contact?.is_primary)?.id)
          : "",
        pets: [],
        pet_id: "",
      }));
      clearCreateFieldError("customer");
      clearCreateFieldError("pet_id");
      setQuickCustomerForm(buildQuickCustomerForm());
      setQuickPetForm(buildQuickPetForm());
      setShowQuickPetForm(true);
      setLookupStatus("found");
      setLookupMessage("Cliente creado. Ahora registra la mascota para agendar la cita.");
      showSnack("Cliente creado correctamente.", "success");
    } catch (error) {
      setLookupStatus("error");
      setLookupMessage(extractErrorMessage(error, "No se pudo crear el cliente."));
      showSnack(extractErrorMessage(error, "No se pudo crear el cliente."), "error");
    } finally {
      setCreatingCustomer(false);
    }
  }, [branchId, clearCreateFieldError, createForm.phone_number, quickCustomerForm.email, quickCustomerForm.name, showSnack]);

  const handleCreatePet = useCallback(async () => {
    if (!branchId || !createForm.customer?.id) return;
    if (!String(quickPetForm.name || "").trim()) {
      showSnack("Debes ingresar el nombre de la mascota.", "warning");
      return;
    }

    setCreatingPet(true);
    try {
      const data = await groomingService.createPet({
        branch_id: branchId,
        customer_id: createForm.customer.id,
        name: String(quickPetForm.name || "").trim(),
        species: quickPetForm.species || "DOG",
        breed: String(quickPetForm.breed || "").trim() || undefined,
        size_category: quickPetForm.size_category || "UNKNOWN",
      });
      const createdPet = data?.pet || null;
      setCreateForm((prev) => {
        const pets = sortPetsByName([...prev.pets, createdPet].filter(Boolean));
        return {
          ...prev,
          pets,
          pet_id: createdPet?.id ? String(createdPet.id) : prev.pet_id,
        };
      });
      clearCreateFieldError("pet_id");
      setQuickPetForm(buildQuickPetForm());
      setShowQuickPetForm(false);
      setLookupStatus("found");
      setLookupMessage("Mascota agregada correctamente.");
      showSnack("Mascota creada correctamente.", "success");
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo crear la mascota."), "error");
    } finally {
      setCreatingPet(false);
    }
  }, [branchId, clearCreateFieldError, createForm.customer?.id, quickPetForm.breed, quickPetForm.name, quickPetForm.size_category, quickPetForm.species, showSnack]);

  const handleCreateAppointment = useCallback(async () => {
    if (!branchId) return;
    const nextErrors = {};
    const serviceTypeId = parseInt(String(createForm.requested_service_type_id || "").trim(), 10);
    const serviceTypeOption =
      serviceTypeOptions.find((option) => Number(option?.id) === serviceTypeId) || null;
    const serviceName = getServiceOptionLabel(serviceTypeOption);
    const durationMinutes = parseInt(String(createForm.duration_minutes || "").trim(), 10);
    const assignedToId = parseInt(String(createForm.assigned_to_id || "").trim(), 10);
    const selectedPet =
      createForm.pets.find((pet) => String(pet.id) === String(createForm.pet_id)) || null;

    if (!createForm.customer?.id) nextErrors.customer = "Cliente requerido.";
    if (!createForm.pet_id) nextErrors.pet_id = "Mascota requerida.";
    if (!Number.isInteger(serviceTypeId) || serviceTypeId <= 0) {
      nextErrors.requested_service_type_id = "Servicio requerido.";
    }
    if (!createForm.scheduled_date) nextErrors.scheduled_date = "Fecha requerida.";
    if (!String(createForm.scheduled_time || "").trim()) nextErrors.scheduled_time = "Hora requerida.";
    if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) {
      nextErrors.duration_minutes = "Duracion estimada invalida.";
    }
    if (!Number.isInteger(assignedToId) || assignedToId <= 0) {
      nextErrors.assigned_to_id = "Responsable requerido.";
    }

    const scheduleValidation = validateScheduleDateTime(
      createForm.scheduled_date,
      createForm.scheduled_time,
      { availability: createAvailability }
    );
    if (scheduleValidation.error) {
      if (String(scheduleValidation.error).toLowerCase().includes("fecha")) {
        nextErrors.scheduled_date = nextErrors.scheduled_date || scheduleValidation.error;
      } else {
        nextErrors.scheduled_time = nextErrors.scheduled_time || scheduleValidation.error;
      }
    }

    if (Object.keys(nextErrors).length) {
      setCreateErrors(nextErrors);
      showSnack("Completa los campos obligatorios de la cita.", "warning");
      return;
    }
    setCreateErrors({});
    setAppointmentConflict(null);

    const complexityLabel =
      complexityOptions.find((option) => option.value === createForm.complexity_level)?.label ||
      String(createForm.complexity_level || "").trim();
    const appointmentNotes = buildAppointmentNotes({
      petSizeLabel: selectedPet?.size_category ? getPetSizeLabel(selectedPet.size_category) : "",
      complexityLabel,
      comment: createForm.requested_notes,
    });

    setSavingAppointment(true);
    try {
      await groomingService.createAppointment({
        branch_id: branchId,
        customer_id: createForm.customer.id,
        responsible_contact_id: createForm.responsible_contact_id ? Number(createForm.responsible_contact_id) : undefined,
        responsible_phone: createForm.phone_number,
        responsible_name: createForm.responsible_contact?.contact_name || createForm.customer?.name || undefined,
        pet_id: Number(createForm.pet_id),
        scheduled_start: scheduleValidation.iso,
        duration_minutes: durationMinutes,
        assigned_to_id: assignedToId,
        service_type_id: serviceTypeId,
        requested_service_name: serviceName,
        requested_notes: appointmentNotes || undefined,
      });
      showSnack("Cita grooming creada correctamente.", "success");
      closeCreateDialog();
      await loadRows();
    } catch (error) {
      const statusCode = error?.response?.status;
      const data = error?.response?.data;
      if (statusCode === 409 && String(data?.code || "").toLowerCase() === "appointment_conflict") {
        setAppointmentConflict(data?.conflict || null);
        setCreateErrors({ form: data?.detail || "Ya existe una cita vigente para este cliente y mascota." });
        showSnack(data?.detail || "Ya existe una cita vigente para este cliente y mascota.", "warning");
        return;
      }
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const inlineErrors = {};
        if (data.customer_id) inlineErrors.customer = String(toArray(data.customer_id)[0] || data.customer_id);
        if (data.pet_id) inlineErrors.pet_id = String(toArray(data.pet_id)[0] || data.pet_id);
        if (data.service_type_id) {
          inlineErrors.requested_service_type_id = String(
            toArray(data.service_type_id)[0] || data.service_type_id
          );
        }
        if (data.requested_service_name) {
          inlineErrors.requested_service_type_id = String(
            toArray(data.requested_service_name)[0] || data.requested_service_name
          );
        }
        if (data.scheduled_start) inlineErrors.scheduled_time = String(toArray(data.scheduled_start)[0] || data.scheduled_start);
        if (data.assigned_to_id) inlineErrors.assigned_to_id = String(toArray(data.assigned_to_id)[0] || data.assigned_to_id);
        if (data.duration_minutes) {
          inlineErrors.duration_minutes = String(toArray(data.duration_minutes)[0] || data.duration_minutes);
        }
        if (data.detail && !inlineErrors.form) inlineErrors.form = String(data.detail);
        if (Object.keys(inlineErrors).length) {
          setCreateErrors(inlineErrors);
        }
      }
      showSnack(extractErrorMessage(error, "No se pudo crear la cita grooming."), "error");
    } finally {
      setSavingAppointment(false);
    }
  }, [branchId, closeCreateDialog, complexityOptions, createAvailability, createForm, loadRows, serviceTypeOptions, showSnack]);

  const handleViewConflictAppointment = useCallback(async () => {
    if (!appointmentConflict?.id || !branchId) return;
    try {
      const full =
        rows.find((row) => String(row.id) === String(appointmentConflict.id)) ||
        (await groomingService.getAppointment(appointmentConflict.id, { branch_id: branchId }));
      setSelectedAppointment(full || null);
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo cargar la cita en conflicto."), "error");
    }
  }, [appointmentConflict?.id, branchId, rows, showSnack]);

  const handleRescheduleConflict = useCallback(async () => {
    if (
      !branchId ||
      !appointmentConflict?.id ||
      !APPOINTMENT_BLOCKING_STATUSES.has(String(appointmentConflict.status || "").toUpperCase()) ||
      !appointmentConflict?.can_reprogram
    ) {
      return;
    }
    const scheduleValidation = validateScheduleDateTime(
      createForm.scheduled_date,
      createForm.scheduled_time,
      { availability: createAvailability }
    );
    const serviceTypeId = parseInt(String(createForm.requested_service_type_id || "").trim(), 10);
    const serviceTypeOption =
      serviceTypeOptions.find((option) => Number(option?.id) === serviceTypeId) || null;
    const serviceName = getServiceOptionLabel(serviceTypeOption);
    const durationMinutes = parseInt(String(createForm.duration_minutes || "").trim(), 10);
    const assignedToId = parseInt(String(createForm.assigned_to_id || "").trim(), 10);
    if (
      scheduleValidation.error ||
      !Number.isInteger(serviceTypeId) ||
      serviceTypeId <= 0 ||
      !serviceName ||
      !Number.isInteger(durationMinutes) ||
      durationMinutes <= 0 ||
      !Number.isInteger(assignedToId) ||
      assignedToId <= 0
    ) {
      setCreateErrors((prev) => ({
        ...prev,
        scheduled_date: String(scheduleValidation.error || "").toLowerCase().includes("fecha")
          ? scheduleValidation.error
          : prev.scheduled_date,
        scheduled_time: String(scheduleValidation.error || "").toLowerCase().includes("fecha")
          ? prev.scheduled_time
          : scheduleValidation.error || prev.scheduled_time,
        requested_service_type_id:
          !Number.isInteger(serviceTypeId) || serviceTypeId <= 0
            ? "Servicio requerido."
            : prev.requested_service_type_id,
        duration_minutes:
          !Number.isInteger(durationMinutes) || durationMinutes <= 0
            ? "Duracion estimada invalida."
            : prev.duration_minutes,
        assigned_to_id:
          !Number.isInteger(assignedToId) || assignedToId <= 0
            ? "Responsable requerido."
            : prev.assigned_to_id,
      }));
      showSnack("Completa fecha, hora, servicio, duracion y responsable para reprogramar.", "warning");
      return;
    }
    setSavingAppointment(true);
    try {
      await groomingService.updateAppointment(appointmentConflict.id, {
        branch_id: branchId,
        scheduled_start: scheduleValidation.iso,
        service_type_id: serviceTypeId,
        requested_service_name: serviceName,
        duration_minutes: durationMinutes,
        assigned_to_id: assignedToId,
      });
      setAppointmentConflict(null);
      setCreateErrors({});
      showSnack("Cita reprogramada correctamente.", "success");
      await loadRows();
      await loadAppointmentContext({ silent: true });
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo reprogramar la cita."), "error");
    } finally {
      setSavingAppointment(false);
    }
  }, [appointmentConflict, branchId, createAvailability, createForm.assigned_to_id, createForm.duration_minutes, createForm.requested_service_type_id, createForm.scheduled_date, createForm.scheduled_time, loadAppointmentContext, loadRows, serviceTypeOptions, showSnack]);

  const handleCancelConflictAppointment = useCallback(async () => {
    if (!branchId || !appointmentConflict?.id) return;
    setSavingAppointment(true);
    try {
      await groomingService.updateAppointment(appointmentConflict.id, {
        branch_id: branchId,
        status: "CANCELED",
        cancellation_reason: "Cancelada para crear una nueva cita.",
      });
      setAppointmentConflict(null);
      showSnack("Cita en conflicto cancelada. Ahora puedes crear una nueva.", "success");
      await loadRows();
      await loadAppointmentContext({ silent: true });
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo cancelar la cita en conflicto."), "error");
    } finally {
      setSavingAppointment(false);
    }
  }, [appointmentConflict?.id, branchId, loadAppointmentContext, loadRows, showSnack]);

  const openRescheduleDialog = useCallback(
    (appointment) => {
      if (!appointment?.id) return;
      const startDate = appointment?.scheduled_start ? new Date(appointment.scheduled_start) : null;
      const parsedNotes = parseAppointmentNotes(appointment?.requested_notes, complexityOptions);
      const inferredServiceId =
        appointment?.service_type?.id
          ? String(appointment.service_type.id)
          : String(
            (
              serviceTypeOptions.find(
                (option) =>
                  getServiceOptionLabel(option).toLowerCase() ===
                  String(appointment?.requested_service_name || "").trim().toLowerCase()
              )?.id || ""
            )
          );
      setRescheduleTarget(appointment);
      setRescheduleErrors({});
      setRescheduleForm({
        scheduled_date: startDate && !Number.isNaN(startDate.getTime()) ? startDate : null,
        scheduled_time: toTimeInputValue(startDate),
        requested_service_type_id: inferredServiceId,
        complexity_level: parsedNotes.complexity_level || "",
        duration_minutes: String(appointment?.duration_minutes || 60),
        assigned_to_id: appointment?.assigned_to?.id ? String(appointment.assigned_to.id) : "",
        requested_notes: parsedNotes.comment || "",
      });
      setRescheduleDialogOpen(true);
    },
    [complexityOptions, serviceTypeOptions]
  );

  const closeRescheduleDialog = useCallback(() => {
    if (savingReschedule) return;
    setRescheduleDialogOpen(false);
    setRescheduleTarget(null);
    setRescheduleErrors({});
    setRescheduleForm(buildRescheduleForm());
    setRescheduleAvailability(null);
    setLoadingRescheduleAvailability(false);
  }, [savingReschedule]);

  const handleRescheduleFieldChange = useCallback((field) => (event, valueFromToggle) => {
    const value = event?.target?.value ?? valueFromToggle ?? "";
    setRescheduleErrors((prev) => {
      if (!prev[field] && !prev.form) return prev;
      const next = { ...prev };
      delete next[field];
      delete next.form;
      return next;
    });
    setRescheduleForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveReschedule = useCallback(async () => {
    if (!branchId || !rescheduleTarget?.id) return;

    const nextErrors = {};
    const scheduleValidation = validateScheduleDateTime(
      rescheduleForm.scheduled_date,
      rescheduleForm.scheduled_time,
      { availability: rescheduleAvailability }
    );
    const serviceTypeId = parseInt(String(rescheduleForm.requested_service_type_id || "").trim(), 10);
    const serviceTypeOption =
      serviceTypeOptions.find((option) => Number(option?.id) === serviceTypeId) || null;
    const serviceName = getServiceOptionLabel(serviceTypeOption);
    const durationMinutes = parseInt(String(rescheduleForm.duration_minutes || "").trim(), 10);
    const assignedToId = parseInt(String(rescheduleForm.assigned_to_id || "").trim(), 10);

    if (scheduleValidation.error) {
      if (String(scheduleValidation.error).toLowerCase().includes("fecha")) {
        nextErrors.scheduled_date = scheduleValidation.error;
      } else {
        nextErrors.scheduled_time = scheduleValidation.error;
      }
    }
    if (!Number.isInteger(serviceTypeId) || serviceTypeId <= 0) {
      nextErrors.requested_service_type_id = "Servicio requerido.";
    }
    if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) {
      nextErrors.duration_minutes = "Duracion estimada invalida.";
    }
    if (!Number.isInteger(assignedToId) || assignedToId <= 0) {
      nextErrors.assigned_to_id = "Responsable requerido.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setRescheduleErrors(nextErrors);
      showSnack("Completa los campos para reprogramar la cita.", "warning");
      return;
    }

    const complexityLabel =
      complexityOptions.find((option) => option.value === rescheduleForm.complexity_level)?.label ||
      String(rescheduleForm.complexity_level || "").trim();
    const petSizeLabel = rescheduleTarget?.pet?.size_category
      ? getPetSizeLabel(rescheduleTarget.pet.size_category)
      : "";
    const requestedNotes = buildAppointmentNotes({
      petSizeLabel,
      complexityLabel,
      comment: rescheduleForm.requested_notes,
    });

    setSavingReschedule(true);
    try {
      await groomingService.updateAppointment(rescheduleTarget.id, {
        branch_id: branchId,
        scheduled_start: scheduleValidation.iso,
        service_type_id: serviceTypeId,
        requested_service_name: serviceName,
        duration_minutes: durationMinutes,
        assigned_to_id: assignedToId,
        requested_notes: requestedNotes || undefined,
      });
      showSnack("Cita reprogramada correctamente.", "success");
      closeRescheduleDialog();
      setSelectedAppointment(null);
      await loadRows();
      await loadAppointmentContext({ silent: true });
    } catch (error) {
      const data = error?.response?.data;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const inlineErrors = {};
        if (data.scheduled_start) {
          inlineErrors.scheduled_time = String(toArray(data.scheduled_start)[0] || data.scheduled_start);
        }
        if (data.service_type_id) {
          inlineErrors.requested_service_type_id = String(
            toArray(data.service_type_id)[0] || data.service_type_id
          );
        }
        if (data.duration_minutes) {
          inlineErrors.duration_minutes = String(toArray(data.duration_minutes)[0] || data.duration_minutes);
        }
        if (data.assigned_to_id) {
          inlineErrors.assigned_to_id = String(toArray(data.assigned_to_id)[0] || data.assigned_to_id);
        }
        if (data.detail) inlineErrors.form = String(data.detail);
        if (Object.keys(inlineErrors).length) {
          setRescheduleErrors(inlineErrors);
        }
      }
      showSnack(extractErrorMessage(error, "No se pudo reprogramar la cita."), "error");
    } finally {
      setSavingReschedule(false);
    }
  }, [
    branchId,
    closeRescheduleDialog,
    complexityOptions,
    loadAppointmentContext,
    loadRows,
    rescheduleForm.assigned_to_id,
    rescheduleForm.complexity_level,
    rescheduleForm.duration_minutes,
    rescheduleForm.requested_notes,
    rescheduleForm.requested_service_type_id,
    rescheduleForm.scheduled_date,
    rescheduleForm.scheduled_time,
    rescheduleAvailability,
    rescheduleTarget,
    serviceTypeOptions,
    showSnack,
  ]);

  const handleConvertToOrder = useCallback(
    async (appointment) => {
      if (!branchId || !appointment?.id || !canManage) return;
      if (isAppointmentInvalidForOrderConversion(appointment)) {
        showSnack("La cita no se puede convertir porque esta cancelada, perdida o vencida.", "warning");
        return;
      }
      const canContinue = await ensureOpenCashSession(
        "Debes tener una sesion de caja abierta para recibir perros y crear ordenes grooming."
      );
      if (!canContinue) return;
      setConvertingId(appointment.id);
      try {
        const data = await groomingService.convertAppointmentToOrder(appointment.id, {
          branch_id: branchId,
        });
        const orderId = data?.order?.id || data?.appointment?.order?.id;
        showSnack("Cita convertida a orden correctamente.", "success");
        await loadRows();
        if (orderId) {
          setDiagnosticDialog({
            open: true,
            orderId,
            initialOrder: data?.order || null,
          });
        }
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo convertir la cita en orden."), "error");
      } finally {
        setConvertingId(null);
      }
    },
    [branchId, canManage, ensureOpenCashSession, loadRows, showSnack]
  );

  const createSlotOptions = useMemo(() => {
    const slots = getSlotsFromAvailability(createAvailability);
    const busySlots = getBusySlotsFromAvailability(createAvailability);
    const occupancyByTime = getSlotOccupancyMapFromAvailability(createAvailability);
    const nowSnapshot = getGuatemalaNowSnapshot(now);
    const todayIso = nowSnapshot.dateKey;
    const selectedIso = toQueryDate(createForm.scheduled_date);
    return slots.map((slot) => {
      const slotMinuteMark = slotToMinutes(slot);
      const isPastToday =
        Boolean(selectedIso) &&
        selectedIso === todayIso &&
        Number.isInteger(slotMinuteMark) &&
        Number.isInteger(nowSnapshot.minutesOfDay) &&
        slotMinuteMark < nowSnapshot.minutesOfDay;
      const occupancy = occupancyByTime[slot] || {};
      const count = Number(occupancy?.count || 0);
      const capacity = Math.max(1, Number(occupancy?.capacity || createAvailability?.business_hours?.max_pets_per_slot || 1));
      const isBusy = busySlots.has(slot) || count >= capacity;
      const isPartial = !isBusy && count > 0;
      const disabled = isPastToday || isBusy;
      return {
        value: slot,
        disabled,
        label: `${slot}${isBusy ? ` (lleno ${count}/${capacity})` : isPartial ? ` (parcial ${count}/${capacity})` : isPastToday ? " (pasado)" : ""}`,
      };
    });
  }, [createAvailability, createForm.scheduled_date, now]);

  const rescheduleSlotOptions = useMemo(() => {
    const slots = getSlotsFromAvailability(rescheduleAvailability);
    const busySlots = getBusySlotsFromAvailability(rescheduleAvailability);
    const occupancyByTime = getSlotOccupancyMapFromAvailability(rescheduleAvailability);
    const nowSnapshot = getGuatemalaNowSnapshot(now);
    const todayIso = nowSnapshot.dateKey;
    const selectedIso = toQueryDate(rescheduleForm.scheduled_date);
    return slots.map((slot) => {
      const slotMinuteMark = slotToMinutes(slot);
      const isPastToday =
        Boolean(selectedIso) &&
        selectedIso === todayIso &&
        Number.isInteger(slotMinuteMark) &&
        Number.isInteger(nowSnapshot.minutesOfDay) &&
        slotMinuteMark < nowSnapshot.minutesOfDay;
      const occupancy = occupancyByTime[slot] || {};
      const count = Number(occupancy?.count || 0);
      const capacity = Math.max(1, Number(occupancy?.capacity || rescheduleAvailability?.business_hours?.max_pets_per_slot || 1));
      const isBusy = busySlots.has(slot) || count >= capacity;
      const isPartial = !isBusy && count > 0;
      const disabled = isPastToday || isBusy;
      return {
        value: slot,
        disabled,
        label: `${slot}${isBusy ? ` (lleno ${count}/${capacity})` : isPartial ? ` (parcial ${count}/${capacity})` : isPastToday ? " (pasado)" : ""}`,
      };
    });
  }, [now, rescheduleAvailability, rescheduleForm.scheduled_date]);

  const createSlotOccupancyRows = useMemo(
    () => toArray(createAvailability?.slots),
    [createAvailability?.slots]
  );
  const rescheduleSlotOccupancyRows = useMemo(
    () => toArray(rescheduleAvailability?.slots),
    [rescheduleAvailability?.slots]
  );

  const selectedCreatePet = useMemo(
    () => createForm.pets.find((pet) => String(pet.id) === String(createForm.pet_id)) || null,
    [createForm.pet_id, createForm.pets]
  );
  const selectedServiceType = useMemo(
    () =>
      serviceTypeOptions.find((option) => {
        return getServiceOptionId(option) === String(createForm.requested_service_type_id || "");
      }) || null,
    [createForm.requested_service_type_id, serviceTypeOptions]
  );
  const selectedGroomer = useMemo(
    () =>
      groomerOptions.find((option) => String(option?.id) === String(createForm.assigned_to_id || "")) || null,
    [createForm.assigned_to_id, groomerOptions]
  );
  const selectedRescheduleServiceType = useMemo(
    () =>
      serviceTypeOptions.find(
        (option) => getServiceOptionId(option) === String(rescheduleForm.requested_service_type_id || "")
      ) || null,
    [rescheduleForm.requested_service_type_id, serviceTypeOptions]
  );
  const selectedRescheduleGroomer = useMemo(
    () =>
      groomerOptions.find((option) => String(option?.id) === String(rescheduleForm.assigned_to_id || "")) || null,
    [groomerOptions, rescheduleForm.assigned_to_id]
  );
  const selectedAppointmentConversionBlocked = isAppointmentInvalidForOrderConversion(selectedAppointment);
  const isConflictReprogrammable =
    APPOINTMENT_BLOCKING_STATUSES.has(String(appointmentConflict?.status || "").toUpperCase()) &&
    Boolean(appointmentConflict?.can_reprogram);

  const orderedRows = useMemo(
    () =>
      [...rows].sort((left, right) => {
        const leftDate = new Date(left?.scheduled_start || 0).getTime();
        const rightDate = new Date(right?.scheduled_start || 0).getTime();
        return leftDate - rightDate;
      }),
    [rows]
  );

  const statusCounts = useMemo(
    () =>
      orderedRows.reduce(
        (accumulator, row) => {
          const categoryKey = getAppointmentCategoryKey(row);
          accumulator.ALL += 1;
          if (accumulator[categoryKey] !== undefined) {
            accumulator[categoryKey] += 1;
          }
          return accumulator;
        },
        {
          ALL: 0,
          SCHEDULED: 0,
          CHECKED_IN: 0,
          IN_PROGRESS: 0,
          COMPLETED: 0,
          CANCELED: 0,
        }
      ),
    [orderedRows]
  );

  const timelineRows = useMemo(() => {
    if (statusTab === "ALL") return orderedRows;
    return orderedRows.filter((row) => getAppointmentCategoryKey(row) === statusTab);
  }, [orderedRows, statusTab]);

  const metricsCards = useMemo(
    () => [
      { key: "ALL", label: "Total", value: statusCounts.ALL, color: theme.palette.text.primary },
      { key: "SCHEDULED", label: "Programadas", value: statusCounts.SCHEDULED, color: STATUS_COLOR_PROGRAMMED },
      { key: "CHECKED_IN", label: "Recepcionadas", value: statusCounts.CHECKED_IN, color: STATUS_COLOR_RECEIVED },
      { key: "IN_PROGRESS", label: "En proceso", value: statusCounts.IN_PROGRESS, color: STATUS_COLOR_IN_PROGRESS },
      { key: "COMPLETED", label: "Completadas", value: statusCounts.COMPLETED, color: STATUS_COLOR_COMPLETED },
      { key: "CANCELED", label: "Canceladas", value: statusCounts.CANCELED, color: theme.palette.text.disabled },
    ],
    [statusCounts, theme.palette.text.disabled, theme.palette.text.primary]
  );

  const nowGuatemalaSnapshot = useMemo(() => getGuatemalaNowSnapshot(now), [now]);
  const isSelectedDayToday = useMemo(() => isSameDay(selectedDay, now), [now, selectedDay]);
  const nowLabel = useMemo(() => formatAgendaTime(now), [now]);
  const dayChipLabel = useMemo(() => formatAgendaDayLabel(selectedDay), [selectedDay]);

  const timelineEntries = useMemo(() => {
    if (!timelineRows.length) return [];

    const entries = [];
    let insertedNowMarker = false;
    timelineRows.forEach((row) => {
      const scheduledDate = new Date(row?.scheduled_start);
      if (
        isSelectedDayToday &&
        !insertedNowMarker &&
        !Number.isNaN(scheduledDate.getTime()) &&
        scheduledDate.getTime() >= now.getTime()
      ) {
        entries.push({ type: "now" });
        insertedNowMarker = true;
      }
      entries.push({ type: "appointment", row });
    });

    if (isSelectedDayToday && !insertedNowMarker) {
      entries.push({ type: "now" });
    }

    return entries;
  }, [isSelectedDayToday, now, timelineRows]);

  const openAdvancedFilters = useCallback(() => {
    setShowAdvancedFilters(true);
  }, []);

  const toggleAdvancedFilters = useCallback(() => {
    setShowAdvancedFilters((prev) => !prev);
  }, []);

  const handleChangeSelectedDay = useCallback((offset) => {
    setSelectedDay((prev) => addDays(prev, offset));
    setDateField("scheduled_start");
    setDateFrom(null);
    setDateTo(null);
    setDayPickerAnchorEl(null);
  }, []);

  const handleGoToToday = useCallback(() => {
    setSelectedDay(startOfDay(new Date()));
    setDateField("scheduled_start");
    setDateFrom(null);
    setDateTo(null);
    setDayPickerAnchorEl(null);
  }, []);

  const handleDismissMissedAlert = useCallback(() => {
    if (!branchId || !missedAlertSignature) return;
    const storageKey = `${MISSED_ALERT_DISMISS_STORAGE_PREFIX}:${branchId}`;
    window.localStorage.setItem(storageKey, missedAlertSignature);
    setMissedAlertDismissed(true);
    setMissedAlertExpanded(false);
  }, [branchId, missedAlertSignature]);

  const handleOpenMissedAppointment = useCallback(
    async (appointmentId) => {
      if (!appointmentId || !branchId) return;
      try {
        const detail =
          rows.find((item) => String(item.id) === String(appointmentId)) ||
          (await groomingService.getAppointment(appointmentId, { branch_id: branchId }));
        setSelectedAppointment(detail || null);
      } catch (error) {
        showSnack(
          extractErrorMessage(error, "No se pudo cargar la cita no asistida."),
          "error"
        );
      }
    },
    [branchId, rows, showSnack]
  );

  if (!canViewPage) {
    return <Alert severity="warning">No tienes permisos para ver citas grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para operar grooming.</Alert>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
        <MainText
          title="Agenda grooming"
          subtitle="Agenda operativa conectada con ordenes y recepcion"
          icon={EventOutlined}
        />
        {canManage ? (
          <Button
            variant="contained"
            onClick={() => {
              resetCreateForm();
              loadAppointmentContext({ silent: true });
              setCreateDialogOpen(true);
            }}
          >
            Nueva cita
          </Button>
        ) : null}
      </Stack>

      {cashStatusKnown && !hasOpenCashSession ? (
        <Alert severity="warning">
          No hay caja abierta en esta sucursal. Puedes crear citas, pero no recibir perros hasta abrir caja.
        </Alert>
      ) : null}
      {missedAppointments.length > 0 && !missedAlertDismissed ? (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Stack spacing={0.75}>
            <Alert
              severity="warning"
              sx={{ py: 0.35 }}
              action={(
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => setMissedAlertExpanded((prev) => !prev)}>
                    {missedAlertExpanded ? "Ocultar" : "Ver lista"}
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleDismissMissedAlert}>
                    Entendido
                  </Button>
                </Stack>
              )}
            >
              {`${missedAppointments.length} cliente${missedAppointments.length === 1 ? "" : "s"} no asistieron a su cita`}
            </Alert>
            <Collapse in={missedAlertExpanded}>
              <Stack spacing={0.75} sx={{ px: 0.5 }}>
                {missedAppointments.slice(0, 8).map((row) => (
                  <Stack
                    key={row.id}
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ md: "center" }}
                    justifyContent="space-between"
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: (themeArg) => alpha(themeArg.palette.warning.main, 0.08),
                    }}
                  >
                    <Typography variant="caption">
                      {row?.customer?.name || "Cliente"} · {row?.pet?.name || "Mascota"} ·{" "}
                      {formatDateTime(row?.scheduled_start)} · {row?.service || "Servicio"}
                    </Typography>
                    <Button size="small" variant="text" onClick={() => handleOpenMissedAppointment(row.id)}>
                      Ver
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          </Stack>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ minWidth: 0, flexWrap: "wrap" }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton size="small" onClick={() => handleChangeSelectedDay(-1)}>
                  <ChevronLeftOutlined fontSize="small" />
                </IconButton>
                <Chip
                  label={dayChipLabel}
                  variant="outlined"
                  onClick={(event) => setDayPickerAnchorEl(event.currentTarget)}
                  clickable
                />
                <Button
                  size="small"
                  startIcon={<TodayOutlined fontSize="small" />}
                  onClick={handleGoToToday}
                >
                  Hoy
                </Button>
                <IconButton size="small" onClick={() => handleChangeSelectedDay(1)}>
                  <ChevronRightOutlined fontSize="small" />
                </IconButton>
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {TIMELINE_STATUS_TABS.map((tab) => (
                  <Chip
                    key={tab.key}
                    clickable
                    size="small"
                    label={`${tab.label} (${statusCounts[tab.key] || 0})`}
                    variant={statusTab === tab.key ? "filled" : "outlined"}
                    color={statusTab === tab.key ? "primary" : "default"}
                    onClick={() => setStatusTab(tab.key)}
                  />
                ))}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" onClick={openAdvancedFilters}>
                Groomer: {assignedToFilter || "todos"}
              </Button>
              <Button
                variant={showAdvancedFilters ? "contained" : "outlined"}
                size="small"
                startIcon={<FilterListOutlined />}
                onClick={toggleAdvancedFilters}
              >
                + Filtros
              </Button>
            </Stack>
          </Stack>

          {showAdvancedFilters ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.25}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
                  <TextField
                    label="Buscar"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Cita, telefono, cliente o mascota"
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    select
                    label="Estado"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value || "all"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Cliente"
                    value={customerFilter}
                    onChange={(event) => setCustomerFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    label="Mascota"
                    value={petFilter}
                    onChange={(event) => setPetFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    label="Asignado"
                    value={assignedToFilter}
                    onChange={(event) => setAssignedToFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    select
                    label="Fecha base"
                    value={dateField}
                    onChange={(event) => setDateField(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    {APPOINTMENT_DATE_FIELD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Con orden"
                    value={hasOrderFilter}
                    onChange={(event) => setHasOrderFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    {BOOL_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={`order-${option.value || "all"}`} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <DatePick
                    name="date_from"
                    label="Desde"
                    value={dateFrom}
                    onChange={handleDateChange}
                    size="small"
                    allowHistoricalDates
                    maxWidth={220}
                    minWidth={180}
                    width="220px"
                  />
                  <DatePick
                    name="date_to"
                    label="Hasta"
                    value={dateTo}
                    onChange={handleDateChange}
                    size="small"
                    allowHistoricalDates
                    maxWidth={220}
                    minWidth={180}
                    width="220px"
                  />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
                    Actualizar
                  </Button>
                  <Button variant="text" onClick={clearFilters}>
                    Limpiar filtros
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Paper>

      <Popover
        open={Boolean(dayPickerAnchorEl)}
        anchorEl={dayPickerAnchorEl}
        onClose={() => setDayPickerAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DateCalendar
            value={dayjs(selectedDay)}
            onChange={(value) => {
              if (!value) return;
              const pickedDate = value.toDate();
              setSelectedDay(startOfDay(pickedDate));
              setDateField("scheduled_start");
              setDateFrom(null);
              setDateTo(null);
              setDayPickerAnchorEl(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(6, minmax(150px, 1fr))", md: "1fr" },
              gap: 1,
              overflowX: { xs: "auto", md: "visible" },
              pb: { xs: 0.5, md: 0 },
            }}
          >
            {metricsCards.map((metric) => (
              <MetricCard
                key={metric.key}
                label={metric.label}
                value={metric.value}
                color={metric.color}
                theme={theme}
              />
            ))}
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, minHeight: 480 }}>
          {loading ? (
            <Stack spacing={1.5}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Stack key={index} direction="row" spacing={1.5}>
                  <Box sx={{ width: 42, pt: 0.5 }}>
                    <Typography variant="caption" color="text.disabled">
                      --:--
                    </Typography>
                  </Box>
                  <Box sx={{ width: 24 }} />
                  <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
                    <Stack spacing={0.9}>
                      <Box sx={{ height: 14, bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: 1 }} />
                      <Box sx={{ height: 12, width: "70%", bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: 1 }} />
                    </Stack>
                  </Paper>
                </Stack>
              ))}
            </Stack>
          ) : timelineEntries.length === 0 ? (
            <Box
              sx={{
                minHeight: 360,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Stack spacing={1}>
                <Typography variant="body2">No hay citas para esta agenda.</Typography>
                <Typography variant="caption" color="text.secondary">
                  Ajusta el dia o abre filtros para buscar otras citas.
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {timelineEntries.map((entry, index) =>
                entry.type === "now" ? (
                  <NowMarker key={`now-${index}`} nowLabel={nowLabel} />
                ) : (
                  <TimelineAppointmentRow
                    key={entry.row.id}
                    appointment={entry.row}
                    now={now}
                    theme={theme}
                    canManage={canManage}
                    convertingId={convertingId}
                    cashStatusKnown={cashStatusKnown}
                    hasOpenCashSession={hasOpenCashSession}
                    onConvert={handleConvertToOrder}
                    onOpenDetail={setSelectedAppointment}
                    onOpenOrder={(orderId) => navigate(`/grooming/orders/${orderId}`)}
                    onReschedule={openRescheduleDialog}
                  />
                )
              )}
            </Stack>
          )}
        </Paper>
      </Box>
      {/* 
      <Dialog
        open={Boolean(selectedAppointment)}
        onClose={() => setSelectedAppointment(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalle de cita grooming</DialogTitle>
        <DialogContent dividers>
          {selectedAppointment ? (
            <Stack spacing={1.25}>
              <Chip
                size="small"
                label={getAppointmentStatusMeta(selectedAppointment).label}
                color={getAppointmentStatusMeta(selectedAppointment).color}
                sx={{ alignSelf: "flex-start" }}
              />
              {selectedAppointment?.is_rescheduled ? (
                <Chip size="small" label="Reprogramada" variant="outlined" color="secondary" sx={{ alignSelf: "flex-start" }} />
              ) : null}
              <Typography variant="body2">
                <strong>Mascota:</strong> {selectedAppointment.pet?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Cliente:</strong> {selectedAppointment.customer?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Telefono:</strong> {selectedAppointment.customer?.phone_number || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Programada:</strong> {formatDateTime(selectedAppointment.scheduled_start)}
              </Typography>
              {selectedAppointment?.original_start_at ? (
                <Typography variant="body2">
                  <strong>Fecha original:</strong> {formatDateTime(selectedAppointment.original_start_at)}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Final estimada:</strong> {formatDateTime(selectedAppointment.scheduled_end)}
              </Typography>
              <Typography variant="body2">
                <strong>Servicio solicitado:</strong> {selectedAppointment.requested_service_name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Tipo de servicio:</strong> {selectedAppointment.service_type?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Asignado:</strong> {selectedAppointment.assigned_to?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Notas:</strong> {selectedAppointment.requested_notes || "-"}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAppointment(null)}>Cerrar</Button>
          {canManage && selectedAppointment?.can_reschedule ? (
            <Button
              variant={String(selectedAppointment?.time_state || "").toUpperCase() === "DELAYED" ? "contained" : "outlined"}
              color={String(selectedAppointment?.time_state || "").toUpperCase() === "DELAYED" ? "warning" : "primary"}
              onClick={() => openRescheduleDialog(selectedAppointment)}
            >
              Reprogramar
            </Button>
          ) : null}
          {selectedAppointment?.order?.id ? (
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/grooming/orders/${selectedAppointment.order.id}`);
                setSelectedAppointment(null);
              }}
            >
              Ver orden
            </Button>
          ) : selectedAppointmentConversionBlocked ? (
            <Button variant="outlined" disabled>
              No convertible
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={
                !canManage ||
                selectedAppointmentConversionBlocked ||
                convertingId === selectedAppointment?.id ||
                (cashStatusKnown && !hasOpenCashSession)
              }
              onClick={async () => {
                const appointment = selectedAppointment;
                setSelectedAppointment(null);
                await handleConvertToOrder(appointment);
              }}
            >
              {convertingId === selectedAppointment?.id ? "Convirtiendo..." : "Convertir a orden"}
            </Button>
          )}
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={Boolean(selectedAppointment)}
        onClose={() => setSelectedAppointment(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "#fffdf7",
            boxShadow: "0 24px 70px rgba(25,25,20,.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.4,
            py: 1.8,
            background: "linear-gradient(135deg, #4f5716 0%, #6f761f 100%)",
            color: "#fff",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.1 }} noWrap>
                Detalle de cita grooming
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: "0.82rem", color: "rgba(255,255,255,.75)" }} noWrap>
                {selectedAppointment?.pet?.name || "Mascota"} · {selectedAppointment?.customer?.name || "Cliente"}
              </Typography>
            </Box>

            {selectedAppointment ? (
              <Chip
                size="small"
                label={getAppointmentStatusMeta(selectedAppointment).label}
                color={getAppointmentStatusMeta(selectedAppointment).color}
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,.16)",
                  color: "#fff",
                }}
              />
            ) : null}
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 2,
            borderColor: "rgba(111,118,31,.16)",
            backgroundColor: "#fbfaf4",
          }}
        >
          {selectedAppointment ? (
            <Stack spacing={1.4}>
              {selectedAppointment?.is_rescheduled ? (
                <Chip
                  size="small"
                  label="Reprogramada"
                  variant="outlined"
                  color="secondary"
                  sx={{ alignSelf: "flex-start", fontWeight: 700, borderRadius: 2 }}
                />
              ) : null}

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid rgba(111,118,31,.16)",
                  backgroundColor: "#fff",
                }}
              >
                <Stack direction="row" spacing={1.4} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(111,118,31,.10)",
                      color: "#4f5716",
                      fontWeight: 800,
                      fontSize: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    {(selectedAppointment.pet?.name || "M").slice(0, 1).toUpperCase()}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#24251f" }} noWrap>
                      {selectedAppointment.pet?.name || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.84rem", color: "#777568" }} noWrap>
                      {selectedAppointment.requested_service_name || selectedAppointment.service_type?.name || "Servicio grooming"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid rgba(111,118,31,.16)",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "#777568",
                  }}
                >
                  Cliente
                </Typography>

                <Stack spacing={0.8}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Nombre</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {selectedAppointment.customer?.name || "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Teléfono</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {selectedAppointment.customer?.phone_number || "-"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid rgba(111,118,31,.16)",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "#777568",
                  }}
                >
                  Programación
                </Typography>

                <Stack spacing={0.8}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Inicio</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {formatDateTime(selectedAppointment.scheduled_start)}
                    </Typography>
                  </Stack>

                  {selectedAppointment?.original_start_at ? (
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Fecha original</Typography>
                      <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                        {formatDateTime(selectedAppointment.original_start_at)}
                      </Typography>
                    </Stack>
                  ) : null}

                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Final estimada</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {formatDateTime(selectedAppointment.scheduled_end)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid rgba(111,118,31,.16)",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "#777568",
                  }}
                >
                  Servicio
                </Typography>

                <Stack spacing={0.8}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Solicitado</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {selectedAppointment.requested_service_name || "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Tipo</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {selectedAppointment.service_type?.name || "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "#777568", fontSize: "0.86rem" }}>Asignado</Typography>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.88rem", textAlign: "right" }}>
                      {selectedAppointment.assigned_to?.name || "-"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid rgba(111,118,31,.16)",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    mb: 0.7,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "#777568",
                  }}
                >
                  Notas
                </Typography>

                <Typography sx={{ fontSize: "0.88rem", color: "#24251f", lineHeight: 1.55 }}>
                  {selectedAppointment.requested_notes || "Sin notas registradas."}
                </Typography>
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            gap: 0.8,
            borderTop: "1px solid rgba(111,118,31,.16)",
            backgroundColor: "#fff",
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={() => setSelectedAppointment(null)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              color: "#4f5716",
            }}
          >
            Cerrar
          </Button>

          {canManage && selectedAppointment?.can_reschedule ? (
            <Button
              variant={String(selectedAppointment?.time_state || "").toUpperCase() === "DELAYED" ? "contained" : "outlined"}
              color={String(selectedAppointment?.time_state || "").toUpperCase() === "DELAYED" ? "warning" : "primary"}
              onClick={() => openRescheduleDialog(selectedAppointment)}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Reprogramar
            </Button>
          ) : null}

          {selectedAppointment?.order?.id ? (
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/grooming/orders/${selectedAppointment.order.id}`);
                setSelectedAppointment(null);
              }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Ver orden
            </Button>
          ) : selectedAppointmentConversionBlocked ? (
            <Button variant="outlined" disabled sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
              No convertible
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={
                !canManage ||
                selectedAppointmentConversionBlocked ||
                convertingId === selectedAppointment?.id ||
                (cashStatusKnown && !hasOpenCashSession)
              }
              onClick={async () => {
                const appointment = selectedAppointment;
                setSelectedAppointment(null);
                await handleConvertToOrder(appointment);
              }}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: "#6f761f",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#4f5716",
                  boxShadow: "none",
                },
              }}
            >
              {convertingId === selectedAppointment?.id ? "Convirtiendo..." : "Convertir a orden"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleDialogOpen} onClose={closeRescheduleDialog} fullWidth maxWidth="sm">
        <DialogTitle>Reprogramar cita grooming</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {rescheduleErrors.form ? <Alert severity="error">{rescheduleErrors.form}</Alert> : null}
            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {rescheduleTarget?.customer?.name || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mascota: {rescheduleTarget?.pet?.name || "-"}
                </Typography>
              </Stack>
            </Paper>

            <Autocomplete
              options={serviceTypeOptions}
              value={selectedRescheduleServiceType}
              isOptionEqualToValue={(option, value) => getServiceOptionId(option) === getServiceOptionId(value)}
              onChange={(_, value) => {
                const suggestedDuration = Number(value?.duration_minutes || 0);
                handleRescheduleFieldChange("requested_service_type_id")(
                  { target: { value: value?.id ? String(value.id) : "" } },
                  value?.id ? String(value.id) : ""
                );
                setRescheduleForm((prev) => ({
                  ...prev,
                  duration_minutes:
                    Number.isFinite(suggestedDuration) && suggestedDuration > 0
                      ? String(parseInt(String(suggestedDuration), 10))
                      : prev.duration_minutes,
                }));
              }}
              getOptionLabel={(option) => getServiceOptionLabel(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Servicio solicitado"
                  error={Boolean(rescheduleErrors.requested_service_type_id)}
                  helperText={rescheduleErrors.requested_service_type_id || "Selecciona un servicio del catalogo."}
                />
              )}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
                Complejidad estimada
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={rescheduleForm.complexity_level || ""}
                onChange={(_, value) => {
                  handleRescheduleFieldChange("complexity_level")(
                    { target: { value: value || "" } },
                    value || ""
                  );
                }}
                sx={{ flexWrap: "wrap", gap: 0.75 }}
              >
                <ToggleButton value="">No definido</ToggleButton>
                {complexityOptions.map((option) => (
                  <ToggleButton key={option.value || option.label} value={option.value || option.label}>
                    {option.label || option.value}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              <TextField
                label="Fecha"
                type="date"
                value={toQueryDate(rescheduleForm.scheduled_date) || ""}
                onChange={(event) => {
                  setRescheduleErrors((prev) => {
                    if (!prev.scheduled_date && !prev.scheduled_time && !prev.form) return prev;
                    const next = { ...prev };
                    delete next.scheduled_date;
                    delete next.scheduled_time;
                    delete next.form;
                    return next;
                  });
                  setRescheduleForm((prev) => ({
                    ...prev,
                    scheduled_date: event.target.value ? new Date(`${event.target.value}T00:00:00`) : null,
                    scheduled_time: "",
                  }));
                }
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: nowGuatemalaSnapshot.dateKey || undefined }}
                error={Boolean(rescheduleErrors.scheduled_date)}
                helperText={rescheduleErrors.scheduled_date || " "}
                fullWidth
              />
              <TextField
                select
                label="Hora"
                value={rescheduleForm.scheduled_time}
                onChange={handleRescheduleFieldChange("scheduled_time")}
                error={Boolean(rescheduleErrors.scheduled_time)}
                helperText={
                  rescheduleErrors.scheduled_time ||
                  (!rescheduleForm.scheduled_date
                    ? "Selecciona primero la fecha."
                    : loadingRescheduleAvailability
                      ? "Cargando disponibilidad..."
                      : buildAvailabilityHelpText(rescheduleAvailability))
                }
                disabled={!rescheduleForm.scheduled_date || loadingRescheduleAvailability}
                fullWidth
              >
                <MenuItem value="">Selecciona un slot</MenuItem>
                {rescheduleSlotOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {rescheduleSlotOccupancyRows.length > 0 ? (
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
                  <Typography variant="caption" color="text.secondary">
                    Slots del dia seleccionado
                  </Typography>
                  <Stack spacing={0.45} sx={{ mt: 0.45, maxHeight: 132, overflowY: "auto", pr: 0.35 }}>
                    {rescheduleSlotOccupancyRows.map((slot, index) => {
                      const pets = toArray(slot?.pets);
                      const count = Number(slot?.count || 0);
                      const capacity = Number(slot?.capacity || 0);
                      const status = String(slot?.status || "AVAILABLE").toUpperCase();
                      const suffix =
                        status === "FULL"
                          ? `Lleno ${count}/${capacity}`
                          : status === "PARTIAL"
                            ? `Parcial ${count}/${capacity}`
                            : `Disponible ${count}/${capacity}`;
                      return (
                        <Typography key={`${slot?.time || "slot"}-${index}`} variant="caption">
                          {`${slot?.time || "--:--"} · ${suffix}${pets.length ? ` · ${pets.map((pet) => `${pet?.pet || "-"} (${pet?.client || "-"})`).join(", ")}` : ""}`}
                        </Typography>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
              <TextField
                label="Duracion estimada (min)"
                type="number"
                value={rescheduleForm.duration_minutes}
                onChange={handleRescheduleFieldChange("duration_minutes")}
                inputProps={{ min: 1, step: 5 }}
                error={Boolean(rescheduleErrors.duration_minutes)}
                helperText={rescheduleErrors.duration_minutes || "Ejemplo: 60"}
                fullWidth
              />
              <Autocomplete
                options={groomerOptions}
                value={selectedRescheduleGroomer}
                isOptionEqualToValue={(option, value) => String(option?.id || "") === String(value?.id || "")}
                onChange={(_, value) =>
                  handleRescheduleFieldChange("assigned_to_id")(
                    { target: { value: value?.id ? String(value.id) : "" } },
                    value?.id ? String(value.id) : ""
                  )
                }
                getOptionLabel={(option) => option?.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Groomer / responsable"
                    error={Boolean(rescheduleErrors.assigned_to_id)}
                    helperText={rescheduleErrors.assigned_to_id || "Selecciona al responsable de la cita."}
                  />
                )}
              />
            </Box>

            <TextField
              label="Comentario (opcional)"
              value={rescheduleForm.requested_notes}
              onChange={handleRescheduleFieldChange("requested_notes")}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRescheduleDialog} disabled={savingReschedule}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveReschedule} disabled={savingReschedule}>
            {savingReschedule ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Nueva cita grooming
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Flujo rapido para detectar cliente, elegir o registrar mascota y agendar en una sola pantalla.
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Stack spacing={2} sx={{ p: 2 }}>
            {createErrors.form ? <Alert severity="error">{createErrors.form}</Alert> : null}
            {appointmentConflict ? (
              <Paper
                variant="outlined"
                sx={(themeArg) => ({
                  p: 1.5,
                  borderColor: themeArg.palette.warning.main,
                  bgcolor: alpha(themeArg.palette.warning.main, 0.08),
                })}
              >
                <Stack spacing={1.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {createErrors.form ||
                      `Ya existe una cita programada para ${appointmentConflict?.pet?.name || "esta mascota"
                      } el ${formatDateTime(appointmentConflict?.scheduled_start)}.`}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button size="small" variant="outlined" onClick={handleViewConflictAppointment}>
                      Ver cita
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleRescheduleConflict}
                      disabled={!isConflictReprogrammable || savingAppointment}
                    >
                      Reprogramar
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={handleCancelConflictAppointment}
                      disabled={savingAppointment}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : null}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Cliente y mascota
                </Typography>

                <TextField
                  label="Telefono"
                  value={createForm.phone_number}
                  onChange={handleCreateFieldChange("phone_number")}
                  inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: lookupLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : null,
                  }}
                  helperText="La busqueda por telefono se realiza automaticamente."
                />

                {lookupStatus === "typing" ? (
                  <Alert severity="info">{lookupMessage}</Alert>
                ) : null}
                {lookupStatus === "found" && createForm.customer ? (
                  <Alert severity="success" icon={<CheckCircleOutline fontSize="inherit" />}>
                    {lookupMessage || `Cliente detectado: ${createForm.customer.name}`}
                  </Alert>
                ) : null}
                {lookupStatus === "not_found" ? (
                  <Alert severity="warning">{lookupMessage}</Alert>
                ) : null}
                {lookupStatus === "error" ? (
                  <Alert severity="error">{lookupMessage}</Alert>
                ) : null}

                {createForm.customer ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    <TextField label="Cliente detectado" value={createForm.customer?.name || ""} fullWidth disabled />
                    <TextField
                      label="Telefono confirmado"
                      value={createForm.customer?.phone_number || createForm.phone_number}
                      fullWidth
                      disabled
                    />
                    {createForm.contacts.length ? (
                      <TextField
                        select
                        label="Responsable/contacto"
                        value={createForm.responsible_contact_id}
                        onChange={handleCreateFieldChange("responsible_contact_id")}
                        fullWidth
                      >
                        {createForm.contacts.map((contact) => (
                          <MenuItem key={contact.id} value={String(contact.id)}>
                            {(contact.contact_name || createForm.customer?.name || "Responsable")} · {contact.phone} · {contact.label_display || contact.relationship || "Contacto"}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                  </Box>
                ) : null}
                {createErrors.customer ? (
                  <Typography variant="caption" color="error">
                    {createErrors.customer}
                  </Typography>
                ) : null}

                {!createForm.customer && lookupStatus === "not_found" ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 1.5,
                      alignItems: "start",
                    }}
                  >
                    <TextField
                      label="Nombre del cliente"
                      value={quickCustomerForm.name}
                      onChange={handleQuickCustomerFieldChange("name")}
                      fullWidth
                    />
                    <TextField
                      label="Correo opcional"
                      value={quickCustomerForm.email}
                      onChange={handleQuickCustomerFieldChange("email")}
                      fullWidth
                    />
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 3" }, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        startIcon={<AddOutlined />}
                        onClick={handleCreateCustomer}
                        disabled={creatingCustomer || lookupLoading}
                      >
                        {creatingCustomer ? "Creando cliente..." : "Crear cliente"}
                      </Button>
                    </Box>
                  </Box>
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Mascota
                  </Typography>
                  {createForm.customer ? (
                    <Button
                      variant={showQuickPetForm ? "text" : "outlined"}
                      startIcon={<PetsOutlined />}
                      onClick={() => setShowQuickPetForm((prev) => !prev)}
                    >
                      {showQuickPetForm ? "Ocultar alta rapida" : "Agregar mascota"}
                    </Button>
                  ) : null}
                </Stack>

                {!createForm.customer ? (
                  <Alert severity="info">Primero detecta o crea un cliente para continuar con la mascota.</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    <TextField
                      select
                      label="Mascota"
                      value={createForm.pet_id}
                      onChange={handleCreateFieldChange("pet_id")}
                      fullWidth
                      disabled={createForm.pets.length === 0}
                      error={Boolean(createErrors.pet_id)}
                      helperText={
                        createErrors.pet_id
                          ? createErrors.pet_id
                          : selectedCreatePet
                            ? `${selectedCreatePet.species === "CAT" ? "Gato" : selectedCreatePet.species === "OTHER" ? "Otro" : "Perro"}${selectedCreatePet.breed ? ` - ${selectedCreatePet.breed}` : ""}`
                            : createForm.pets.length === 0
                              ? "Este cliente aun no tiene mascotas disponibles en Grooming."
                              : "Selecciona una mascota existente."
                      }
                    >
                      {createForm.pets.length === 0 ? (
                        <MenuItem value="">Sin mascotas registradas</MenuItem>
                      ) : (
                        createForm.pets.map((pet) => (
                          <MenuItem key={pet.id} value={String(pet.id)}>
                            {pet.name}{pet.breed ? ` · ${pet.breed}` : ""}{pet.customer_name ? ` · ${pet.customer_name}` : ""}
                          </MenuItem>
                        ))
                      )}
                    </TextField>

                    {(showQuickPetForm || createForm.pets.length === 0) && (
                      <Paper
                        variant="outlined"
                        sx={(theme) => ({
                          p: 1.5,
                          bgcolor: theme.palette.action.hover,
                        })}
                      >
                        <Stack spacing={1.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Alta rapida de mascota
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                              gap: 1.5,
                            }}
                          >
                            <TextField
                              label="Nombre de la mascota"
                              value={quickPetForm.name}
                              onChange={handleQuickPetFieldChange("name")}
                              fullWidth
                            />
                            <TextField
                              select
                              label="Especie"
                              value={quickPetForm.species}
                              onChange={handleQuickPetFieldChange("species")}
                              fullWidth
                            >
                              {PET_SPECIES_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="Raza"
                              value={quickPetForm.breed}
                              onChange={handleQuickPetFieldChange("breed")}
                              fullWidth
                            />
                            <TextField
                              select
                              label="Tamano"
                              value={quickPetForm.size_category}
                              onChange={handleQuickPetFieldChange("size_category")}
                              fullWidth
                            >
                              {PET_SIZE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                          <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            {showQuickPetForm ? (
                              <Button
                                variant="text"
                                onClick={() => {
                                  setQuickPetForm(buildQuickPetForm());
                                  setShowQuickPetForm(false);
                                }}
                              >
                                Cancelar
                              </Button>
                            ) : null}
                            <Button
                              variant="contained"
                              startIcon={<AddOutlined />}
                              onClick={handleCreatePet}
                              disabled={creatingPet}
                            >
                              {creatingPet ? "Guardando mascota..." : "Guardar mascota"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Servicio
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <Autocomplete
                    options={serviceTypeOptions}
                    value={selectedServiceType}
                    isOptionEqualToValue={(option, value) => getServiceOptionId(option) === getServiceOptionId(value)}
                    onChange={(_, value) => {
                      const suggestedDuration = Number(value?.duration_minutes || 0);
                      clearCreateFieldError("requested_service_type_id");
                      setCreateForm((prev) => ({
                        ...prev,
                        requested_service_type_id: value?.id ? String(value.id) : "",
                        requested_service_name: getServiceOptionLabel(value),
                        duration_minutes:
                          Number.isFinite(suggestedDuration) && suggestedDuration > 0
                            ? String(parseInt(String(suggestedDuration), 10))
                            : prev.duration_minutes,
                      }));
                      setAppointmentConflict(null);
                    }}
                    getOptionLabel={(option) => getServiceOptionLabel(option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Servicio solicitado"
                        error={Boolean(createErrors.requested_service_type_id)}
                        helperText={
                          createErrors.requested_service_type_id ||
                          "Selecciona un servicio del catalogo."
                        }
                      />
                    )}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
                      Complejidad estimada
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={createForm.complexity_level || ""}
                      onChange={(_, value) => {
                        clearCreateFieldError("complexity_level");
                        setCreateForm((prev) => ({ ...prev, complexity_level: value || "" }));
                        setAppointmentConflict(null);
                      }}
                      sx={{ flexWrap: "wrap", gap: 0.75 }}
                    >
                      <ToggleButton value="">No definido</ToggleButton>
                      {complexityOptions.map((option) => (
                        <ToggleButton key={option.value || option.label} value={option.value || option.label}>
                          {option.label || option.value}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Nivel de trabajo esperado para la cita.
                    </Typography>
                  </Box>
                  <TextField
                    label="Duracion estimada (min)"
                    type="number"
                    value={createForm.duration_minutes}
                    onChange={handleCreateFieldChange("duration_minutes")}
                    inputProps={{ min: 1, step: 5 }}
                    error={Boolean(createErrors.duration_minutes)}
                    helperText={createErrors.duration_minutes || "Ejemplo: 60"}
                    fullWidth
                  />
                  <TextField
                    label="Tamano de mascota"
                    value={selectedCreatePet ? getPetSizeLabel(selectedCreatePet.size_category) : "-"}
                    helperText="Tomado de la ficha de la mascota."
                    fullWidth
                    disabled
                  />
                </Box>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Fecha y responsable
                  </Typography>
                  {contextLoading ? <CircularProgress size={16} /> : null}
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    label="Fecha"
                    type="date"
                    value={toQueryDate(createForm.scheduled_date) || ""}
                    onChange={(event) =>
                      handleCreateDateChange(
                        "scheduled_date",
                        event.target.value ? new Date(`${event.target.value}T00:00:00`) : null
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: nowGuatemalaSnapshot.dateKey || undefined }}
                    error={Boolean(createErrors.scheduled_date)}
                    helperText={createErrors.scheduled_date || " "}
                    fullWidth
                  />
                  <TextField
                    select
                    label="Hora"
                    value={createForm.scheduled_time}
                    onChange={handleCreateFieldChange("scheduled_time")}
                    error={Boolean(createErrors.scheduled_time)}
                    helperText={
                      createErrors.scheduled_time ||
                      (!createForm.scheduled_date
                        ? "Selecciona primero la fecha."
                        : loadingCreateAvailability
                          ? "Cargando disponibilidad..."
                          : buildAvailabilityHelpText(createAvailability))
                    }
                    disabled={!createForm.scheduled_date || loadingCreateAvailability}
                    fullWidth
                  >
                    <MenuItem value="">Selecciona un slot</MenuItem>
                    {createSlotOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {createSlotOccupancyRows.length > 0 ? (
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
                      <Typography variant="caption" color="text.secondary">
                        Slots del dia seleccionado
                      </Typography>
                      <Stack spacing={0.45} sx={{ mt: 0.45, maxHeight: 132, overflowY: "auto", pr: 0.35 }}>
                        {createSlotOccupancyRows.map((slot, index) => {
                          const pets = toArray(slot?.pets);
                          const count = Number(slot?.count || 0);
                          const capacity = Number(slot?.capacity || 0);
                          const status = String(slot?.status || "AVAILABLE").toUpperCase();
                          const suffix =
                            status === "FULL"
                              ? `Lleno ${count}/${capacity}`
                              : status === "PARTIAL"
                                ? `Parcial ${count}/${capacity}`
                                : `Disponible ${count}/${capacity}`;
                          return (
                            <Typography key={`${slot?.time || "slot"}-${index}`} variant="caption">
                              {`${slot?.time || "--:--"} · ${suffix}${pets.length ? ` · ${pets.map((pet) => `${pet?.pet || "-"} (${pet?.client || "-"})`).join(", ")}` : ""}`}
                            </Typography>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null}
                  <Autocomplete
                    options={groomerOptions}
                    value={selectedGroomer}
                    isOptionEqualToValue={(option, value) => String(option?.id || "") === String(value?.id || "")}
                    onChange={(_, value) => {
                      clearCreateFieldError("assigned_to_id");
                      setCreateForm((prev) => ({
                        ...prev,
                        assigned_to_id: value?.id ? String(value.id) : "",
                      }));
                      setAppointmentConflict(null);
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Groomer / responsable"
                        error={Boolean(createErrors.assigned_to_id)}
                        helperText={createErrors.assigned_to_id || "Selecciona al responsable de la cita."}
                      />
                    )}
                  />
                </Box>
                <TextField
                  label="Comentario (opcional)"
                  value={createForm.requested_notes}
                  onChange={handleCreateFieldChange("requested_notes")}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeCreateDialog}
            disabled={savingAppointment || lookupLoading || creatingCustomer || creatingPet}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAppointment}
            disabled={savingAppointment || lookupLoading || creatingCustomer || creatingPet}
          >
            {savingAppointment ? "Guardando..." : "Guardar cita"}
          </Button>
        </DialogActions>
      </Dialog>

      <GroomingDiagnosticDialog
        open={diagnosticDialog.open}
        branchId={branchId}
        orderId={diagnosticDialog.orderId}
        initialOrder={diagnosticDialog.initialOrder}
        canManage={canManage}
        onClose={() =>
          setDiagnosticDialog({
            open: false,
            orderId: null,
            initialOrder: null,
          })
        }
        onContinue={(orderData) => {
          const nextOrderId = orderData?.id || diagnosticDialog.orderId;
          setDiagnosticDialog({
            open: false,
            orderId: null,
            initialOrder: null,
          });
          if (nextOrderId) {
            navigate(`/grooming/orders/${nextOrderId}`);
          }
        }}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default GroomingAppointmentsPage;
